import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/SimpleAuthContext';
import API_CONFIG from '../../config/apiConfig';
import { useToast } from '../../contexts/ToastContext';

const PlacementModal = ({ isOpen, onClose, registrationId, onSuccess }) => {
    const { makeAuthenticatedRequest } = useAuth();
    const { showToast } = useToast();
    const [formData, setFormData] = useState({
        company_name: '',
        position: '',
        start_date: '',
        end_date: '',
        supervisor_name: '',
        supervisor_email: '',
        supervisor_phone: ''
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await makeAuthenticatedRequest(
                API_CONFIG.registrations.placements(),
                {
                    method: 'POST',
                    body: JSON.stringify({
                        registration: registrationId,
                        ...formData,
                        status: 'active'
                    })
                }
            );
            if (response.ok) {
                showToast('Placement created successfully', 'success');
                onSuccess();
                onClose();
            } else {
                const errorData = await response.json();
                let errorMessage = 'Failed to create placement';
                
                if (errorData.non_field_errors) {
                    errorMessage = errorData.non_field_errors[0];
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (Object.keys(errorData).length > 0) {
                     // Get first error from any field
                     const firstKey = Object.keys(errorData)[0];
                     const firstError = Array.isArray(errorData[firstKey]) ? errorData[firstKey][0] : errorData[firstKey];
                     errorMessage = `${firstKey}: ${firstError}`;
                }
                
                showToast(errorMessage, 'error');
            }
        } catch (error) {
            console.error(error);
            showToast('Error creating placement', 'error');
        }
    };

    return (
        <div
            className="fixed inset-0 z-[1100] flex items-center justify-center overflow-y-auto bg-slate-950/60 px-4 py-6 backdrop-blur-sm sm:px-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="placement-modal-title"
        >
            <div className="w-full max-w-xl">
                <div className="overflow-hidden rounded-2xl border border-white/70 bg-white shadow-2xl ring-1 ring-black/5">
                    <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-6 py-5">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">Recruitment</p>
                            <h3 id="placement-modal-title" className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                                Manage Placement
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Close placement dialog"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    <form className="max-h-[78vh] overflow-y-auto px-6 py-6 sm:px-8" onSubmit={handleSubmit}>
                        <div className="space-y-4 text-left">
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-slate-700">Company Name</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleChange}
                                    className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-slate-700">Position</label>
                                <input
                                    type="text"
                                    name="position"
                                    value={formData.position}
                                    onChange={handleChange}
                                    className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-slate-700">Start Date</label>
                                    <input
                                        type="date"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-slate-700">End Date</label>
                                    <input
                                        type="date"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-slate-700">Supervisor Name</label>
                                <input
                                    type="text"
                                    name="supervisor_name"
                                    value={formData.supervisor_name}
                                    onChange={handleChange}
                                    className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-slate-700">Supervisor Email</label>
                                <input
                                    type="email"
                                    name="supervisor_email"
                                    value={formData.supervisor_email}
                                    onChange={handleChange}
                                    className="block w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-4 focus:ring-amber-100"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600"
                            >
                                Create Placement
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PlacementModal;
